'use client';
/*
 * Documentation:
 * BasicCard03 — https://app.subframe.com/6fc7f5fbb098/library?component=BasicCard03_542b4712-35a8-4b81-bd12-8d7de521357b
 * Icon Button — https://app.subframe.com/6fc7f5fbb098/library?component=Icon+Button_af9405b1-8c54-4e01-9786-5aad308224f6
 * Icon with background — https://app.subframe.com/6fc7f5fbb098/library?component=Icon+with+background_c5d68c0e-4c0c-4cff-8d8c-6ff334859b3a
 */

import React from 'react';
import { FeatherArrowRight } from '@subframe/core';
import { FeatherCheck } from '@subframe/core';
import { FeatherFileText } from '@subframe/core';
import { FeatherPlus } from '@subframe/core';
import { FeatherUser } from '@subframe/core';
import * as SubframeCore from '@subframe/core';
import * as SubframeUtils from '../utils';
import { IconButton } from '../common/IconButton';
import { IconWithBackground } from '../common/IconWithBackground';

interface BasicCard03RootProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: 'option-1' | 'user';
  className?: string;
}

const BasicCard03Root = React.forwardRef<HTMLDivElement, BasicCard03RootProps>(
  function BasicCard03Root(
    {
      icon = <FeatherFileText />,
      title,
      description,
      variant = 'option-1',
      className,
      ...otherProps
    }: BasicCard03RootProps,
    ref,
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          'group/542b4712 bg-default-background hover:bg-brand-50 flex h-full w-full cursor-pointer items-center gap-4 rounded-[16px] border border-solid border-neutral-200 px-4 py-4 shadow-md',
          {
            'rounded-none border-none bg-transparent px-0 py-0 shadow-none': variant === 'user',
          },
          className,
        )}
        ref={ref}
        {...otherProps}
      >
        <IconWithBackground
          className={SubframeUtils.twClassNames({ hidden: variant === 'user' })}
          variant={variant === 'user' ? 'neutral' : undefined}
          size="large"
          icon={icon}
          square={true}
        />
        <IconWithBackground
          className={SubframeUtils.twClassNames('hidden', {
            flex: variant === 'user',
          })}
          variant={variant === 'user' ? 'neutral' : 'brand'}
          size={variant === 'user' ? 'medium' : 'x-small'}
          icon={variant === 'user' ? <FeatherUser /> : <FeatherCheck />}
          square={false}
        />
        <div className="flex shrink-0 grow basis-0 flex-col items-start gap-1">
          {title ? (
            <span className="text-body-bold font-body-bold text-default-font w-full">{title}</span>
          ) : null}
          {description ? (
            <span className="text-caption font-caption text-default-font w-full">
              {description}
            </span>
          ) : null}
        </div>
        <IconButton
          className={SubframeUtils.twClassNames('hidden', {
            flex: variant === 'user',
          })}
          disabled={false}
          variant={variant === 'user' ? 'brand-tertiary' : 'neutral-tertiary'}
          size="medium"
          icon={variant === 'user' ? <FeatherArrowRight /> : <FeatherPlus />}
          loading={false}
        />
      </div>
    );
  },
);

export const BasicCard03 = BasicCard03Root;
